import { ThunkAction } from 'redux-thunk';
import { Dispatch } from 'react-redux';
import Notify from 'store/utils/Notify';
import ErrorHandler from 'store/utils/ErrorHandler';
import { EnvironmentItem } from 'scenes/ProjectSettings/scenes/Environments/EnvironmentsList/Environment/types';
import * as types from './actionTypes';
// eslint-disable-next-line import/no-cycle
import * as api from './api';
import { PartialRootState, EnvironmentsActions } from './types';

// Exporting only this actionCreator in order
// to import it in the spec file and test it
export const envRequest = () => ({
  type: types.ENVIRONMENTS_REQUEST,
});

const createEnvSuccess = (item: EnvironmentItem) => ({
  type: types.CREATE_ENVIRONMENT_SUCCESS,
  payload: item,
});

const createEnvError = (error: string) => ({
  type: types.CREATE_ENVIRONMENT_ERROR,
  error,
});

export type ThunkResult<R> = ThunkAction<
  R,
  PartialRootState,
  undefined,
  EnvironmentsActions
>;

export const createEnvironment = ({
  environmentName,
  dashboardURL,
  emailOrUsername,
  applicationName,
}: {
  environmentName: string;
  dashboardURL: string;
  emailOrUsername: string;
  applicationName: string;
}): ThunkResult<void> => async (dispatch: Dispatch<EnvironmentsActions>) => {
  try {
    dispatch(envRequest());

    const { data: envItem } = await api.createEnvironmentRequest(
      environmentName,
      dashboardURL,
      emailOrUsername,
      applicationName,
    );

    dispatch(createEnvSuccess(envItem));
  } catch (error) {
    Notify.error({
      title: `Unable to create the '${environmentName}' environment.`,
      description: ErrorHandler.getNotificationError(error),
    });
    dispatch(createEnvError(error));
  }
};

const fetchEnvListSuccess = (items: EnvironmentItem[]) => ({
  type: types.FETCH_ENVIRONMENTS_LIST_SUCCESS,
  payload: items,
});

const fetchEnvListError = (error: string) => ({
  type: types.FETCH_ENVIRONMENTS_LIST_ERROR,
  error,
});

export const fetchEnvironmentsList = (): ThunkResult<void> => async (
  dispatch: Dispatch<EnvironmentsActions>,
) => {
  try {
    dispatch(envRequest());

    const {
      data: { results: envItems },
    } = await api.getEnvironmentsListRequest();

    dispatch(fetchEnvListSuccess(envItems));
  } catch (error) {
    Notify.error({
      title: `Unable to get the environments list.`,
      description: ErrorHandler.getNotificationError(error),
    });
    dispatch(fetchEnvListError(error));
  }
};

const deleteEnvError = (error: string) => ({
  type: types.DELETE_ENVIRONMENT_ERROR,
  error,
});

export const deleteEnvironment = (
  environment: EnvironmentItem,
): ThunkResult<void> => async (dispatch: Dispatch<EnvironmentsActions>) => {
  try {
    dispatch(envRequest());

    await api.deleteEnvironmentRequest(environment.id);

    // Here we are having the same issue with the tests
    // Dispatch accept only actions and not thunks. Maybe
    // we can update the types and see if we can resolve this
    // @ts-ignore
    dispatch(fetchEnvironmentsList());

    Notify.success({
      title: `Success`,
      description: `Environment with the name: ${environment.name} deleted.`,
    });
  } catch (error) {
    Notify.error({
      title: `Error`,
      description: `Unable to delete the environment with the name: ${environment.name}`,
    });
    dispatch(deleteEnvError(error));
  }
};

const editEnvError = (error: string) => ({
  type: types.EDIT_ENVIRONMENT_ERROR,
  error,
});

export const editEnvironment = (
  id: string,
  environmentName: string,
  dashboardURL: string,
  emailOrUsername: string,
  applicationName: string,
  setDefault: boolean,
): ThunkResult<void> => async (dispatch: Dispatch<EnvironmentsActions>) => {
  try {
    dispatch(envRequest());

    await api.updateEnvironmentRequest(
      id,
      environmentName,
      dashboardURL,
      emailOrUsername,
      applicationName,
      setDefault,
    );

    // @ts-ignore
    dispatch(fetchEnvironmentsList());

    Notify.success({
      title: `Success`,
      description: `The environment was updated successfully.`,
    });
  } catch (error) {
    Notify.error({
      title: `Error`,
      description: ErrorHandler.getNotificationError(error),
    });
    dispatch(editEnvError(error));
  }
};

export const setDefaultEnvironment = (
  id: string,
  environmentName: string,
  setDefault: boolean,
): ThunkResult<void> => async (dispatch: Dispatch<EnvironmentsActions>) => {
  try {
    dispatch(envRequest());

    await api.setDefaultEnvironmentRequest(id, setDefault);

    // @ts-ignore
    dispatch(fetchEnvironmentsList());

    Notify.success({
      title: `Success`,
      description: `The environment ${environmentName} is set to default.`,
    });
  } catch (error) {
    Notify.error({
      title: `Error`,
      description: `Unable to set the environment ${environmentName} as default.`,
    });
    dispatch(editEnvError(error));
  }
};
